import React, { useEffect, FC } from "react";
import { useSelector } from "react-redux";
import { selectImageToken } from "../../../features/userSlice";
import { getFixedFlg } from "../../Header/Header";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import {
  Button,
  IconButton,
  TextareaAutosize,
} from "@material-ui/core";
import RedoIcon from "@material-ui/icons/Redo";
import UndoIcon from "@material-ui/icons/Undo";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    toolArea: {
      display: "flex",
      justifyContent: "space-between",
      flexWrap: "nowrap",
      marginTop: "10px",
    },
    toggleArea: {
      borderRadius: "100vh",
      backgroundColor: "#666",
      opacity: "0.4",

    },
    toggleButtonChecked: {
      borderRadius: "100vh",
      fontWeight: "bold",
      backgroundColor: "#fff",
      color: "#00ac4c",
      height: "100%",
    },
    toggleButtonUnChecked: {
      borderRadius: "100vh",
      fontWeight: "bold",
      backgroundColor: "#666",
      height: "100%",
    },
    clearArea: {
      marginRight: "10px",
    },
    handwriteClear: {
      borderRadius: "100vh",
      fontWeight: "bold",
      backgroundColor: "transparent",
      color: "#e60012",
      border: "#e60012 solid"
    },
    handwriteClearDisabled: {
      borderRadius: "100vh",
      fontWeight: "bold",
      backgroundColor: "transparent",
      color: "#3333331a",
      border: "rgba(51,51,51,.1019607843) solid"
    },
    textCommentArea: {
      width: "220px",
      // iPad縦
      "@media (min-width: 830px)": {
        width: "200px",
      },
      // iPad横
      "@media (min-width: 1100px)": {
        width: "200px",
      },
      // GWPC
      "@media (min-width: 1200px)": {
        width: "220px",
      },
    },
    handwrite: {
      width: "224px",
      // iPad縦
      "@media (min-width: 830px)": {
        width: "204px",
      },
      // iPad横
      "@media (min-width: 1100px)": {
        width: "204px",
      },
      // GWPC
      "@media (min-width: 1200px)": {
        width: "224px",
      },
      touchAction: "pinch-zoom",
      border: "1px solid",
      borderColor: "#00cc5b",
      height: "194px",
      borderRadius: "8px",
      margin: "10px",
    },
  })
);

export const HandwriteUpdate = { // 手書き入力の更新区分
  Non: "0",
  Update: "1",
  Delete: "2",
}

const SwitchTextArea: FC<{
  comment: string | undefined;
  commentFilepathGcs: string[];
  commentHandwriteFlag: string;
  changeComment: (e: React.ChangeEvent<HTMLTextAreaElement>) => void; // テキストコメント変更関数
  updateCavasContext: (context: CanvasRenderingContext2D) => void; // canvas更新関数
  updateCommentHandwriteFlag: (val: string) => void; // コメント手書きフラグ更新関数
  updateHandwriteUpdateClass: (val: string) => void; // 手書き入力更新区分更新関数
}> = ({ comment, commentFilepathGcs, commentHandwriteFlag, changeComment, updateCavasContext, updateCommentHandwriteFlag, updateHandwriteUpdateClass }) => {

  const defaultLineWidth: number = 2; // デフォルトの線の太さ
  const defaultLineColor: string = "black"; // デフォルトの線の色
  // 座標用
  interface Point {
    x: number;
    y: number;
  }

  const classes = useStyles();
  const canvasRef = React.useRef<HTMLCanvasElement>(null); // 手入力用
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null); // テキストエリア
  const [canvasContext, setCanvasContext] = React.useState<CanvasRenderingContext2D | null>(null); // 手入力用canvasのcontext
  const [moving, setMoving] = React.useState(false); // 移動中であること
  const [isHandwrite, setIsHandwrite] = React.useState<boolean>(commentHandwriteFlag === "1");
  const [editing, setEditing] = React.useState<boolean>(false);
  const [history, setHistory] = React.useState<Point[][]>([]); // 描画履歴
  const [redoHistory, setRedoHistory] = React.useState<Point[][]>([]); // やり直し用の描画履歴
  const [currentPath, setCurrentPath] = React.useState<Point[]>([]); // 現在描画中のパス

  useEffect(() => {
    // テキスト・手書きのボタン切り替え
    updateCommentHandwriteFlag(isHandwrite ? "1" : "0");

    if (isHandwrite) {
      // canvas
      const canvas: HTMLCanvasElement | null = canvasRef.current;
      if (!canvas) return;
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
      const context: CanvasRenderingContext2D | null = canvas.getContext("2d");
      if (!context) return;
      refreshCanvas(context, () => {});
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.lineWidth = defaultLineWidth;
      context.strokeStyle = defaultLineColor;
      context.lineCap = "round";
      context.lineJoin = "round";
      setCanvasContext(context);
      updateCavasContext(context);
  
      // 設定初期化
      setMoving(false);
      setEditing(false);
      setHistory([]);
      setRedoHistory([]);
      setCurrentPath([]);

      updateHandwriteUpdateClass(HandwriteUpdate.Non);
    }
  }, [isHandwrite]);

  /**
   * canvasを更新する
   * @param {CanvasRenderingContext2D} canvasContext - canvasのcontext
   * @param {() => void} callback - 画像読み込み後に実行するコールバック
   */
  const refreshCanvas = (canvasContext: CanvasRenderingContext2D, callback: () => void): void => {
    if (!commentFilepathGcs || !commentFilepathGcs[0]) {
      callback();
      return;
    }
    const img = new Image();
    img.src = getImageFile(commentFilepathGcs[0]);
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const {width, height} = canvasContext.canvas;
      canvasContext.drawImage(img, 0, 0, width, height);
      setEditing(true);
      callback();
    };
    img.onerror = () => {
      callback();
    }
  };
  
  const imageToken = useSelector(selectImageToken);
  function getImageFile(imagePath: string) {
    if (imagePath === undefined || imagePath === "") {
      return `${window.location.origin}/image/noimage.png`;
    }
    return (
      process.env.REACT_APP_CLOUD_STORAGE_URL +
      imagePath +
      "?access_token=" +
      imageToken
    );
  }

  // トグルボタン：テキスト押下時
  const clickToggleText = (): void => {
    setIsHandwrite(false);
    // 手書き内容初期化
    if (!canvasContext || !canvasContext.canvas) return;
    canvasContext.clearRect(0, 0, canvasContext.canvas.width, canvasContext.canvas.height);
  };
  // トグルボタン：手書き押下時
  const clickToggleHandwrite = (): void => {
    setIsHandwrite(true);
    // テキストエリア初期化
    const dummyEvent = { target: { value: "" } };
    changeComment(dummyEvent as React.ChangeEvent<HTMLTextAreaElement>);
  }; 
  // クリアボタン押下
  const clickClear = (): void => {
    updateHandwriteUpdateClass(HandwriteUpdate.Delete);
    setEditing(false);
    setMoving(false);
    setHistory([]);
    setRedoHistory([]);
    if (!canvasContext || !canvasContext.canvas) return;
    const {width, height} = canvasContext.canvas;
    canvasContext.clearRect(0, 0, width, height);
    canvasContext.fillStyle = "#ffffff";
    canvasContext.fillRect(0, 0, width, height);
  };
  /**
   * 一筆戻る
   */
  const handleUndo = () => {
    if (history.length === 0) return;
    const newHistory = [...history];
    const lastPath = newHistory.pop();
    if (lastPath) {
      setRedoHistory([...redoHistory, lastPath]);
    }
    setHistory(newHistory);
    redrawCanvas(newHistory);
    updateHandwriteUpdateClass(HandwriteUpdate.Update);
  };

  /**
   * 一筆進む
   */
  const handleRedo = () => {
    if (redoHistory.length === 0) return;
    const newRedoHistory = [...redoHistory];
    const path = newRedoHistory.pop();
    if (path) {
      const newHistory = [...history, path];
      setHistory(newHistory);
      redrawCanvas(newHistory);
    }
    setRedoHistory(newRedoHistory);
    updateHandwriteUpdateClass(HandwriteUpdate.Update);
  };
  
  /**
   * canvasを再描画する
   * @param {Point[][]} paths - 描画するパスの配列
   */
  const redrawCanvas = (paths: Point[][]) => {
    if (!canvasContext) return;
    const { width, height } = canvasContext.canvas;
    canvasContext.clearRect(0, 0, width, height);
    canvasContext.fillStyle = "#ffffff";
    canvasContext.fillRect(0, 0, width, height);
    refreshCanvas(canvasContext, () => {
      paths.forEach(path => {
        if(path.length === 0) return;
        canvasContext.beginPath();
        canvasContext.moveTo(path[0].x, path[0].y);
        for (let i = 1; i < path.length; i++) {
          canvasContext.lineTo(path[i].x, path[i].y);
        }
        canvasContext.stroke();
      });
    });
  };
  

  // タッチの始め
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasContext) return;
    const offset = canvasContext.canvas.getBoundingClientRect();
    const offsetX = e.touches[0].clientX - offset.left;
    const offsetY = e.touches[0].clientY - offset.top;
    startOperation(offsetX, offsetY);
  };

  // タッチの終わり
  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    endOperation();
    e.preventDefault();
  };

  // タッチで線を引く
  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    // 座標位置取得が必要なモードになっていないとき
    if (!moving) {
      return;
    }
    // 2本指でのタッチ時
    if(e.touches.length >= 2) {
      return;
    };

    if (!canvasContext) return;
    const canvas: HTMLCanvasElement = canvasContext.canvas;
    const offset = canvas.getBoundingClientRect();
    let offsetX = e.changedTouches[0].clientX - offset.left;
    let offsetY = e.changedTouches[0].clientY - offset.top;
    moveOperation(offsetX, offsetY);
  };

  // ドラッグの始め
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const { offsetX, offsetY } = e.nativeEvent;
    startOperation(offsetX, offsetY);
  };

  // ドラッグの終わり
  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    endOperation();
  };

  // ドラッグ中。線を引く、クラップ範囲の指定等を行う。
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // 座標位置取得が必要なモードになっていないとき
    if (!moving) {
      return;
    }

    moveOperation(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
  };

  /**
   * 描画操作開始（mousedown, touchstart）
   * @param {number} offsetX - X座標
   * @param {number} offsetY - Y座標
   */
  const startOperation = (offsetX: number, offsetY: number): void => {
    setMoving(true);
    setEditing(true);
    updateHandwriteUpdateClass(HandwriteUpdate.Update);
    const newPath = [{ x: offsetX, y: offsetY }];
    setCurrentPath(newPath);
    setRedoHistory([]); // やり直し履歴をクリア
  };

  /**
   * 描画操作終了（mouseup, touchend）
   */
  const endOperation = (): void => {
    setMoving(false);
    if(currentPath.length > 0) {
      setHistory([...history, currentPath]);
    }
    setCurrentPath([]);
  };

  /**
   * 描画操作中（mousemove, touchmove）
   * @param {number} offsetX - X座標
   * @param {number} offsetY - Y座標
   */
  const moveOperation = (offsetX: number, offsetY: number): void => {
    if (!moving) return;

    setEditing(true);
    updateHandwriteUpdateClass(HandwriteUpdate.Update);

    if (!canvasContext) return;
    const canvas: HTMLCanvasElement = canvasContext.canvas;
     // 座標補正
    if (offsetX < 0) {
      offsetX = 0;
    } else if (canvas.width < offsetX) {
      offsetX = canvas.width;
    }
    if (offsetY < 0) {
      offsetY = 0;
    } else if (canvas.height < offsetY) {
      offsetY = canvas.height;
    }

    const lastPoint = currentPath[currentPath.length -1];
    const newPath = [...currentPath, { x: offsetX, y: offsetY }];
    setCurrentPath(newPath);

    // 線を引く
    canvasContext.beginPath();
    canvasContext.moveTo(lastPoint.x, lastPoint.y);
    canvasContext.lineTo(offsetX, offsetY);
    canvasContext.stroke();
  };


  return (
    <>
      <div className={classes.toolArea}>
        <div className={classes.toggleArea}>
          <Button
            variant="contained" 
            component="span" 
            className={isHandwrite ? classes.toggleButtonUnChecked : classes.toggleButtonChecked} 
            size="small" 
            style={{ pointerEvents: getFixedFlg() ? "none" : "auto" }}
            onClick={clickToggleText}
          >
            ■テキスト
          </Button>
          <Button 
            variant="contained" 
            component="span" 
            className={isHandwrite ? classes.toggleButtonChecked : classes.toggleButtonUnChecked}
            size="small"
            style={{ pointerEvents: getFixedFlg() ? "none" : "auto" }}
            onClick={clickToggleHandwrite}
          >
            ■手書き
          </Button>
        </div>
        <div className={classes.clearArea}>
          {/* 一筆戻るボタン */}
          <IconButton
            className={(isHandwrite && history.length > 0 && !getFixedFlg()) ? classes.handwriteClear : classes.handwriteClearDisabled}
            disabled={!(isHandwrite && history.length > 0 && !getFixedFlg())}
            onClick={handleUndo}
            size="small"
          >
            <UndoIcon />
          </IconButton>
          {/* 一筆進むボタン */}
          <IconButton
            className={(isHandwrite && redoHistory.length > 0 && !getFixedFlg()) ? classes.handwriteClear : classes.handwriteClearDisabled}
            disabled={!(isHandwrite && redoHistory.length > 0 && !getFixedFlg())}
            onClick={handleRedo}
            size="small"
          >
            <RedoIcon />
          </IconButton>
          {/* クリアボタン */}
          <Button 
            variant="contained" 
            component="span" 
            className={(isHandwrite && editing && !getFixedFlg()) ? classes.handwriteClear : classes.handwriteClearDisabled} 
            disabled={!(isHandwrite && editing && !getFixedFlg())}
            size="small" 
            onClick={clickClear}
          >
            クリア
          </Button>
        </div>
      </div>
      {!isHandwrite ? (
        <TextareaAutosize
          className={classes.textCommentArea}
          rowsMax={16}
          aria-label="store-input-comment-area"
          rowsMin={16}
          maxLength={4000}
          placeholder={
              getFixedFlg()
              ? "その他コメント"
              : "その他コメントがあればご入力ください"
          }
          value={comment}
          onChange={(e) => {
              changeComment(e);
          }}
          style={{
              height: "190px",
              fontSize: "16px",
              borderRadius: "8px",
              margin: "10px",
          }}
          ref={textAreaRef}
        />
      ) : (
        <canvas
          className={classes.handwrite}
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchMove={handleTouchMove}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          style={{ pointerEvents: getFixedFlg() ? "none" : "auto", userSelect: "none", WebkitTouchCallout: "none", WebkitUserSelect: "none"}}
        />
      )}
    </>
  )
}
export default SwitchTextArea;
